import {
  DeleteTwoTone,
  DownloadOutlined,
  EditTwoTone,
  PrinterOutlined,
} from "@ant-design/icons";
import { Dropdown, Menu, message, Popconfirm, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useLocation, useNavigate } from "react-router";
import ImportMultipleVehiclePrint, {
  downloadMultipleVehicle,
} from "./ImportMultipleVehiclePrint";
import ExportMultipleVehiclePrint, {
  downloadExportMultipleVehicle,
} from "./ExportMultipleVehiclePrint";

import {
  EXPORT_VEHICLE_ASSIGNMENT,
  IMPORT_VEHICLE_ASSIGNMENT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import ListTable from "common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import {
  EXPORT_VEHICLE_ASSIGNMENT_DELETE_PERMISSION,
  EXPORT_VEHICLE_ASSIGNMENT_EDIT_PERMISSION,
  EXPORT_VEHICLE_ASSIGNMENT_STORE_PERMISSION,
  IMPORT_VEHICLE_ASSIGNMENT_DELETE_PERMISSION,
  IMPORT_VEHICLE_ASSIGNMENT_EDIT_PERMISSION,
  IMPORT_VEHICLE_ASSIGNMENT_STORE_PERMISSION,
} from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";

const ImportVehicleAssignment = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const isExport = routerPath?.startsWith("/export/");
  const shipmentLabel = isExport ? "Export" : "Import";
  const basePath = isExport ? "/export" : "/import";
  const apiEndpoint = isExport
    ? EXPORT_VEHICLE_ASSIGNMENT
    : IMPORT_VEHICLE_ASSIGNMENT;
  const storePermission = isExport
    ? EXPORT_VEHICLE_ASSIGNMENT_STORE_PERMISSION
    : IMPORT_VEHICLE_ASSIGNMENT_STORE_PERMISSION;
  const editPermission = isExport
    ? EXPORT_VEHICLE_ASSIGNMENT_EDIT_PERMISSION
    : IMPORT_VEHICLE_ASSIGNMENT_EDIT_PERMISSION;
  const deletePermission = isExport
    ? EXPORT_VEHICLE_ASSIGNMENT_DELETE_PERMISSION
    : IMPORT_VEHICLE_ASSIGNMENT_DELETE_PERMISSION;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [printRecord, setPrintRecord] = useState(null);
  const printRef = useRef(null);
  const pendingPrint = useRef(false);
  const printFn = useReactToPrint({
    content: () => printRef.current,
    documentTitle: isExport
      ? "Export Shipment for Multiple Vehicle"
      : "Import Shipment for Multiple Vehicle",
  });
  const printFnRef = useRef(printFn);
  printFnRef.current = printFn;

  // Antd
  const { Title } = Typography;

  // Router
  const navigate = useNavigate();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllVehicleAssignments = useCallback(
    async (filterValues) => {
      setLoading(true);
      setError(null);

      const { page: requestedPage, ...requestFilter } = filterValues || {};
      const pageNumber = requestedPage != null ? requestedPage : currentPage;
      if (requestedPage != null && Number(requestedPage) !== Number(currentPage)) {
        setCurrentPage(Number(requestedPage));
      }
      const query = `${apiEndpoint}?page=${pageNumber}`;

      const response = await getData(query, false, requestFilter);

      if (response && response?.data?.code === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setError("Could not load this list.");
        message.error("Error getting vehicle assignment data");
      }
    },
    [currentPage, apiEndpoint],
  );

  // Effects
  useEffect(() => {
    getAllVehicleAssignments();
  }, [refresh, getAllVehicleAssignments]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedAssignment = async (id) => {
    setLoading(true);
    const response = await deleteData(`${apiEndpoint}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Vehicle Assignment Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Error deleting vehicle assignment");
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const loadAssignment = async (id) => {
    const response = await getData(`${apiEndpoint}/${id}`);
    if (response?.data?.code === 200) return response.data.data;
    message.error("Unable to load this shipment");
    return null;
  };

  const printAssignment = async (id) => {
    const data = await loadAssignment(id);
    if (!data) return;
    pendingPrint.current = true;
    setPrintRecord(data);
  };

  const downloadAssignment = async (id) => {
    const data = await loadAssignment(id);
    if (!data) return;
    (isExport ? downloadExportMultipleVehicle : downloadMultipleVehicle)(data);
  };

  useEffect(() => {
    if (!printRecord || !pendingPrint.current) return undefined;
    const timer = setTimeout(() => {
      pendingPrint.current = false;
      printFnRef.current();
    }, 300);
    return () => clearTimeout(timer);
  }, [printRecord]);

  const cellOrEmpty = (value) => value || "N/A";

  const tableColumns = [
    ...(isExport
      ? [
          {
            title: "Buyer",
            key: "buyer",
            render: (_, record) =>
              cellOrEmpty(
                [
                  ...new Set(
                    (record?.assigned_vehicles || [])
                      .map((item) => item?.buyer_info?.name)
                      .filter(Boolean),
                  ),
                ].join(", "),
              ),
          },
          {
            title: "Buying Office",
            key: "buying_office",
            render: (_, record) =>
              cellOrEmpty(
                [
                  ...new Set(
                    (record?.assigned_vehicles || [])
                      .map((item) => item?.buying_office_info?.name)
                      .filter(Boolean),
                  ),
                ].join(", "),
              ),
          },
          {
            title: "Export Carton",
            dataIndex: "export_carton",
            key: "export_carton",
          },
          {
            title: "Garment Piece Quantity",
            dataIndex: "garment_piece_qty",
            key: "garment_piece_qty",
          },
        ]
      : [
          {
            title: "Invoice Number",
            key: "invoice_number",
            render: (_, record) =>
              cellOrEmpty(
                record?.invoice_number || record?.invoice_info?.invoice_number,
              ),
          },
          {
            title: "LC Number",
            key: "b2b_lc_number",
            render: (_, record) =>
              cellOrEmpty(
                record?.b2b_lc_numnber ||
                  record?.b2b_lc_info?.b2b_lc_number,
              ),
          },
          {
            title: "Supplier",
            key: "supplier",
            render: (_, record) =>
              cellOrEmpty(
                record?.supplier ||
                  record?.invoice_info?.supplier_info?.name ||
                  record?.b2b_lc_info?.supplier_info?.name,
              ),
          },
          {
            title: "Sales Contract",
            key: "sales_contract",
            render: (_, record) =>
              cellOrEmpty(
                record?.sales_contract ||
                  record?.sales_contract_info?.reference_no,
              ),
          },
          {
            title: "Buyer",
            key: "buyer",
            render: (_, record) =>
              cellOrEmpty(
                record?.buyer ||
                  record?.sales_contract_info?.buyer_info?.name ||
                  record?.invoice_info?.buyer_name,
              ),
          },
        ]),
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <span onClick={() => printAssignment(record?.id)}>
                        <PrinterOutlined />
                        <span style={{ marginLeft: 5 }}>Print</span>
                      </span>
                    ),
                    key: "print",
                  },
                  {
                    label: (
                      <span onClick={() => downloadAssignment(record?.id)}>
                        <DownloadOutlined />
                        <span style={{ marginLeft: 5 }}>Download</span>
                      </span>
                    ),
                    key: "download",
                  },
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedAssignment(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([deletePermission]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            navigate(
                              `${basePath}/edit-vehicle-assignment/${record?.id}`,
                            );
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([editPermission]),
                  },
                ]}
              />
            }
          />
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={tableData}
        fetchTableData={getAllVehicleAssignments}
        filterName="import_vehicle_assignment"
      />
    ),
    [showFilter, tableData, getAllVehicleAssignments],
  );

  return (
    <>
      <div style={{ position: "absolute", left: -10000, top: 0 }}>
        <div ref={printRef}>
          {printRecord ? (
            isExport ? (
              <ExportMultipleVehiclePrint record={printRecord} />
            ) : (
              <ImportMultipleVehiclePrint record={printRecord} />
            )
          ) : null}
        </div>
      </div>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                addButtonText={
                  hasPermission([storePermission])
                    ? "Add New"
                    : "No Permission"
                }
                disabled={!hasPermission([storePermission])}
                handleAddItemOpen={() =>
                  navigate(`${basePath}/add-vehicle-assignment`)
                }
              />
            }
            title={
              <Title level={5}>{shipmentLabel} Shipment for Multiple Vehicle</Title>
            }
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={error}
                onRetry={() => getAllVehicleAssignments()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                bordered
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default ImportVehicleAssignment;
